import axios from 'axios';
import { API_URL } from '../config';

// Rate limiting configuration
const RATE_LIMIT_WINDOW = 60000; // 1 minute
const MAX_REQUESTS = 100;

// Track API usage
const apiUsage = {
  location: { count: 0, resetTime: Date.now() + RATE_LIMIT_WINDOW },
  email: { count: 0, resetTime: Date.now() + RATE_LIMIT_WINDOW },
  phone: { count: 0, resetTime: Date.now() + RATE_LIMIT_WINDOW }
};

// Check and update rate limits
const checkRateLimit = (service) => {
  const now = Date.now();
  if (now > apiUsage[service].resetTime) {
    apiUsage[service].count = 0;
    apiUsage[service].resetTime = now + RATE_LIMIT_WINDOW;
  }
  
  if (apiUsage[service].count >= MAX_REQUESTS) {
    throw new Error(`Rate limit exceeded for ${service} service. Please try again later.`);
  }
  
  apiUsage[service].count++;
};

// Location Services
export const getLocationFromZip = async (zipCode) => {
  try {
    checkRateLimit('location');
    
    // Try primary service (Zippopotam)
    try {
      const response = await axios.get(`https://api.zippopotam.us/us/${zipCode}`);
      return {
        success: true,
        data: response.data,
        service: 'zippopotam'
      };
    } catch (error) {
      // Fallback to IPAPI
      const response = await axios.get(`http://ip-api.com/json/${zipCode}?fields=status,message,country,countryCode,region,regionName,city,zip,lat,lon`);
      return {
        success: true,
        data: response.data,
        service: 'ipapi'
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
      service: 'unknown'
    };
  }
};

// Email Verification Services
export const verifyEmail = async (email) => {
  try {
    checkRateLimit('email');
    
    // Try primary service (Abstract API)
    try {
      const response = await axios.get(`https://emailvalidation.abstractapi.com/v1/?api_key=${process.env.REACT_APP_ABSTRACT_API_KEY}&email=${email}`);
      return {
        success: true,
        data: response.data,
        service: 'abstract'
      };
    } catch (error) {
      // Fallback to APILayer
      const response = await axios.get(`https://api.apilayer.com/email_verification/${email}`, {
        headers: { 'apikey': process.env.REACT_APP_APILAYER_KEY }
      });
      return {
        success: true,
        data: response.data,
        service: 'apilayer'
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
      service: 'unknown'
    };
  }
};

// Phone Verification Services
export const verifyPhone = async (phone) => {
  try {
    checkRateLimit('phone');
    
    // Try primary service (NumVerify)
    try {
      const response = await axios.get(`http://apilayer.net/api/validate?access_key=${process.env.REACT_APP_NUMVERIFY_KEY}&number=${phone}`);
      return {
        success: true,
        data: response.data,
        service: 'numverify'
      };
    } catch (error) {
      // Fallback to MessageBird
      const response = await axios.get(`https://rest.messagebird.com/lookup/${phone}`, {
        headers: { 'Authorization': `AccessKey ${process.env.REACT_APP_MESSAGEBIRD_KEY}` }
      });
      return {
        success: true,
        data: response.data,
        service: 'messagebird'
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
      service: 'unknown'
    };
  }
};

// Service Status Check
export const checkServiceStatus = async (service) => {
  const services = {
    location: {
      primary: 'https://api.zippopotam.us/us/90210',
      fallback: 'http://ip-api.com/json/90210'
    },
    email: {
      primary: 'https://emailvalidation.abstractapi.com/v1/',
      fallback: 'https://api.apilayer.com/email_verification/test@example.com'
    },
    phone: {
      primary: 'http://apilayer.net/api/validate',
      fallback: 'https://rest.messagebird.com/lookup/1234567890'
    }
  };

  const endpoints = services[service];
  const results = {
    primary: { status: 'offline', error: null },
    fallback: { status: 'offline', error: null }
  };

  // Check primary service
  try {
    await axios.get(endpoints.primary);
    results.primary.status = 'online';
  } catch (error) {
    results.primary.error = error.message;
  }

  // Check fallback service
  try {
    await axios.get(endpoints.fallback);
    results.fallback.status = 'online';
  } catch (error) {
    results.fallback.error = error.message;
  }

  return results;
}; 